import { useEffect, useMemo, useRef, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import isEmpty from 'lodash/isEmpty';

import { logger } from 'utils/logger';

import LoginRoutes from './LoginRoutes';
import MainRoutes from './MainRoutes';
import { useAppContext } from 'store/AppProvider';

type RouteType = 'login' | 'no-permission' | 'main';

const getRouteType = (token: string | undefined, permissionRolesLength: number): RouteType => {
    if (!token) return 'login';
    return permissionRolesLength ? 'main' : 'no-permission';
};

const Router = () => {
    const { appState } = useAppContext();
    const token = appState.user.user?.token;
    const permissionRoles = appState.generalData.permissionAvailableRoles;

    const [routerKey, setRouterKey] = useState(0);
    const currentRouteTypeRef = useRef<RouteType | null>(null);

    const routeType = getRouteType(token, permissionRoles.length);

    useEffect(() => {
        try {
            if (routeType !== currentRouteTypeRef.current) {
                currentRouteTypeRef.current = routeType;
                setRouterKey((prev) => prev + 1);
            }
        } catch (error) {
            logger.error(Error('Error resolving router:'), { cause: error });
        }
    }, [routeType]);

    const routes = useMemo(() => {
        if (!token) return LoginRoutes;
        return permissionRoles.length ? MainRoutes.Routes : MainRoutes.NoPermissionRoutes;
    }, [routeType]);

    // Create the router only when routes change
    const router = useMemo(() => {
        if (isEmpty(routes)) return null;

        return createBrowserRouter(routes, {
            future: {
                v7_relativeSplatPath: true,
            },
        });
    }, [routes]);

    if (!router) return null;

    return (
        <RouterProvider
            router={router}
            key={routerKey}
            future={{
                v7_startTransition: true,
            }}
        />
    );
};

export default Router;
